import { useEffect, useState } from "react";
import { useDraftRows, UnsavedBar } from "@/components/useDraftRows";
import { Plus, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";

export async function openPdf(path) {
  try {
    const r = await api.get(path, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function ListInput({ value, onChange, testId }) {
  const [raw, setRaw] = useState((value || []).join(", "));
  useEffect(() => { setRaw((cur) => (cur.split(",").map((x) => x.trim()).filter(Boolean).join(",") === (value || []).join(",") ? cur : (value || []).join(", "))); }, [value]);
  return <Input className="h-8" data-testid={testId} value={raw} placeholder="comma separated" onChange={(e) => { setRaw(e.target.value); onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean)); }} />;
}

function MultiChips({ value, onChange, options, testId, labelOf }) {
  const v = value || [];
  return (
    <div className="flex flex-wrap gap-1" data-testid={testId}>
      {options.map((o) => { const on = v.includes(o); return (
        <button type="button" key={o} data-testid={`${testId}-${o}`} aria-pressed={on} onClick={() => onChange(on ? v.filter((x) => x !== o) : [...v, o])}
          className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${on ? "border-[#13294B] bg-[#13294B] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>{labelOf ? labelOf(o) : o.replace(/_/g, " ")}</button>); })}
    </div>
  );
}

export function Cell({ col, value, onChange, testId, row }) {
  if (col.disabledIf?.(row)) return <span className="text-xs text-slate-400" data-testid={testId}>n/a</span>;
  if (col.type === "multi") return <MultiChips value={value} onChange={onChange} options={col.options} testId={testId} labelOf={col.labelOf} />;
  if (col.type === "creatable")
    return <><Input className="h-8" list={`${testId}-list`} data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value.trim().toLowerCase().replace(/\s+/g, "_"))} placeholder="Pick or type" />
      <datalist id={`${testId}-list`}>{col.options.map((o) => <option key={o} value={o} />)}</datalist></>;
  if (col.type === "bool") return <input type="checkbox" data-testid={testId} checked={!!value} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-[#13294B]" />;
  if (col.type === "select")
    return (
      <select data-testid={testId} title={col.labelOf ? col.labelOf(value) : value} className="po-select !h-8 w-full min-w-0" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {col.blank && <option value="">{col.blank}</option>}
        {col.options.map((o) => <option key={o} value={o}>{col.labelOf ? col.labelOf(o) : o}</option>)}
      </select>
    );
  if (col.type === "list") return <ListInput value={value} onChange={onChange} testId={testId} />;
  return <Input className="h-8 w-full min-w-[64px]" data-testid={testId} title={value == null ? undefined : String(value)} type={col.type === "number" ? "number" : "text"} value={value ?? ""} onChange={(e) => onChange(col.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
}

export function EditableTable({ columns, rows, onSave, testId, readOnly, blank = {}, filter, onRemove, onDiscard }) {
  const { data, setData, dirty, discard } = useDraftRows(rows, testId, undefined, onDiscard);
  const set = (i, k, v) => setData((d) => d.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const remove = async (r, i) => { if (!onRemove || (await onRemove(r)) === true) setData((d) => d.filter((_, j) => j !== i)); };
  const shown = filter ? data.filter((r) => filter(r)).length : data.length;
  return (
    <div data-testid={testId}>
      <UnsavedBar dirty={dirty} onDiscard={discard} testId={testId} />
      <div className="overflow-x-auto">
        <table className="po-table">
          <thead><tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}{!readOnly && <th />}</tr></thead>
          <tbody>
            {data.map((r, i) => (!filter || filter(r)) && (
              <tr key={i}>
                {columns.map((c) => <td key={c.key} className="!py-1.5" style={{ minWidth: c.width || 110 }}><div className="flex items-center gap-1.5"><Cell col={c} row={r} value={r[c.key]} onChange={(v) => set(i, c.key, v)} testId={`${testId}-${i}-${c.key}`} />
                  {c.suffix && !c.disabledIf?.(r) && <span className="whitespace-nowrap text-xs text-slate-400" data-testid={`${testId}-${i}-${c.key}-unit`}>{c.suffix(r)}</span>}</div></td>)}
                {!readOnly && <td className="!py-1.5"><button data-testid={`${testId}-remove-${i}`} className="text-slate-400 hover:text-rose-600" onClick={() => remove(r, i)}><Trash2 className="h-4 w-4" /></button></td>}
              </tr>
            ))}
            {filter && shown === 0 && <tr><td colSpan={columns.length + 1} className="py-6 text-center text-sm text-slate-400" data-testid={`${testId}-no-match`}>No rows match the filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {!readOnly && (
        <div className="flex gap-2 border-t border-slate-100 p-3">
          <Button size="sm" variant="outline" data-testid={`${testId}-add`} onClick={() => setData([...data, { ...blank }])}><Plus className="mr-1 h-4 w-4" />Add row</Button>
          <Button size="sm" data-testid={`${testId}-save`} onClick={() => onSave(data.map(({ _draft, ...r }) => r))} className="bg-[#13294B] hover:bg-[#1c3a66]"><Save className="mr-1 h-4 w-4" />Save changes</Button>
        </div>
      )}
    </div>
  );
}

export function KeyValueForm({ fields, value, onSave, testId, readOnly }) {
  const [v, setV] = useState(value || {});
  useEffect(() => setV(value || {}), [value]);
  return (
    <div className="space-y-3 p-5" data-testid={testId}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map((f) => (
          <label key={f.key} className="block space-y-1"><span className="text-sm font-medium text-slate-700">{f.label}</span>
            <div className="flex items-center gap-2"><Input type="number" data-testid={`${testId}-${f.key}`} disabled={readOnly} value={v[f.key] ?? ""} onChange={(e) => setV({ ...v, [f.key]: e.target.value === "" ? null : Number(e.target.value) })} />
              {f.suffix && <span className="whitespace-nowrap text-xs text-slate-500" data-testid={`${testId}-${f.key}-unit`}>{f.suffix}</span>}</div>
          </label>
        ))}
      </div>
      {!readOnly && <Button size="sm" data-testid={`${testId}-save`} onClick={() => onSave(v)} className="bg-[#13294B] hover:bg-[#1c3a66]"><Save className="mr-1 h-4 w-4" />Save</Button>}
    </div>
  );
}
